import { db } from '../firebase';
import type { User } from 'firebase/auth';
import { makeApiCall } from '../utils/apiConfig';
import {
  doc,
  getDoc,
  updateDoc,
  deleteDoc,
  collection,
  query,
  where,
  orderBy,
  limit,
  getDocs,
  onSnapshot,
  writeBatch,
  Unsubscribe,
} from 'firebase/firestore';

export interface UserProfile {
  uid: string;
  email: string;
  displayName?: string;
  role: 'admin' | 'super_admin' | 'user' | 'moderator';
  userType?: 'store' | 'vendor_supplier' | 'group'; // Made optional for onboarding flow. 'group' = a buying group / head office parent org (managed_groups signup), never a store or vendor persona.
  organizationId?: string;
  primaryOrganizationId?: string; // Backend uses this field
  productQaEnabled?: boolean; // Grants access to the Product QA review queue
  status: 'active' | 'inactive' | 'suspended' | 'pending';
  termsAcceptance?: {
    termsAccepted: boolean;
    privacyAccepted: boolean;
    acceptedAt?: Date;
    termsVersion?: string;
    privacyVersion?: string;
  };
  vendorInfo?: {
    companyName: string;
    contactPerson: string;
    phone?: string;
    website?: string;
    productCategories: string[];
    feedUrl?: string;
    mapPricingEnabled: boolean;
    integrationStatus: 'pending' | 'active' | 'suspended';
    feedStats?: {
      totalProducts: number;
      lastSyncAt?: Date;
      syncFrequency: 'daily' | 'weekly' | 'monthly' | 'manual';
      errorCount: number;
    };
    createdAt: Date;
    updatedAt: Date;
  };
  createdAt: any;
  updatedAt: any;
  lastLoginAt?: Date;
  emailVerified: boolean;
  photoUrl?: string;
  preferences?: {
    locale?: {
      language?: string;
      region?: string;
      currency?: string;
      timezone?: string;
      state?: string;
    };
    notifications: {
      newCameras: boolean;
      mapPricingUpdates: boolean;
      systemUpdates: boolean;
      marketing: boolean;
    };
    privacy: {
      profileVisibility: 'public' | 'private' | 'friends';
      showEmail: boolean;
      showLastLogin: boolean;
      doNotSellOrShare?: boolean;
      limitSensitiveUse?: boolean;
    };
    api: {
      rateLimit: number;
      allowedEndpoints: string[];
    };
  };
  metadata?: {
    signupSource: 'email' | 'google' | 'admin';
    ipAddress?: string;
    userAgent?: string;
    timezone?: string;
    language?: string;
    totalApiCalls: number;
    lastApiCallAt?: Date;
  };
}

/** What the signed-in user may set on their own profile (`POST /session/profile`). */
export interface SelfProfileInput {
  displayName?: string;
  userType?: 'store' | 'vendor_supplier';
  signupSource?: 'email' | 'google';
}

export interface Notification {
  id: string;
  userId: string;
  type: 
    | 'new_camera'
    | 'map_pricing_update'
    | 'system_update'
    | 'user_role_changed'
    | 'account_suspended'
    | 'welcome'
    | 'organization_joined'
    | 'map_violation'
    | 'product_added'
    | 'product_updated'
    | 'price_changed'
    | 'integration_sync_completed'
    | 'integration_sync_failed'
    | 'shopify_listing_hold'
    | 'product_announcement';
  title: string;
  message: string;
  read: boolean;
  createdAt: any;
  data?: any;
}

class UserService {
  async getUserProfile(uid: string): Promise<UserProfile | null> {
    try {
      const userDoc = await getDoc(doc(db, 'userProfiles', uid));
      if (userDoc.exists()) {
        const data = userDoc.data();
        // Map primaryOrganizationId to organizationId for compatibility
        // Keep both fields in case frontend checks either one
        return {
          ...data,
          organizationId: data.primaryOrganizationId || data.organizationId,
        } as UserProfile;
      }
      return null;
    } catch (error) {
      console.error('Error getting user profile:', error);
      return null;
    }
  }

  /**
   * Create the signed-in user's profile on first sign-in, or save the fields
   * they may set themselves. The browser never writes `userProfiles`
   * directly: role, status, organization and the verified flag are decided by
   * the server (`POST /session/profile`).
   */
  async saveOwnProfile(user: User, input: SelfProfileInput = {}): Promise<UserProfile> {
    const token = await user.getIdToken();
    // No live-write confirmation or session retry: this is the caller's own
    // profile, and it runs before the email address is verified.
    const response = await makeApiCall(
      '/session/profile',
      {
        method: 'POST',
        headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' },
        body: JSON.stringify(input),
      },
      false
    );
    const body = await response.json().catch(() => ({}));
    if (!response.ok || !body?.success) {
      throw new Error(body?.message || `Could not save the profile (HTTP ${response.status})`);
    }
    return body.data as UserProfile;
  }

  async isAdmin(uid: string): Promise<boolean> {
    const profile = await this.getUserProfile(uid);
    return profile?.role === 'admin' || profile?.role === 'super_admin';
  }

  private notificationsQuery(userId: string) {
    return query(
      collection(db, 'notifications'),
      where('userId', '==', userId),
      orderBy('createdAt', 'desc'),
      limit(100)
    );
  }

  async getNotifications(userId: string): Promise<Notification[]> {
    try {
      const snapshot = await getDocs(this.notificationsQuery(userId));
      return snapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data()
      } as Notification));
    } catch (error) {
      console.error('Error getting notifications:', error);
      return [];
    }
  }

  /**
   * Subscribe to the user's notifications in real time.
   * Returns the unsubscribe function.
   */
  subscribeToNotifications(
    userId: string,
    callback: (notifications: Notification[]) => void
  ): Unsubscribe {
    return onSnapshot(
      this.notificationsQuery(userId),
      snapshot => {
        callback(snapshot.docs.map(doc => ({
          id: doc.id,
          ...doc.data()
        } as Notification)));
      },
      error => {
        console.error('Error subscribing to notifications:', error);
      }
    );
  }

  async markNotificationAsRead(notificationId: string): Promise<void> {
    await updateDoc(doc(db, 'notifications', notificationId), {
      read: true,
    });
  }

  /**
   * Marks every unread notification read, not just the newest 100 the bell
   * lists. Reading only that window left older unread rows behind, so the
   * badge stayed at "99+" after "Mark all read".
   */
  async markAllNotificationsAsRead(userId: string): Promise<void> {
    const BATCH_SIZE = 400;
    // Each pass removes up to BATCH_SIZE docs from the unread set; stop on a short page.
    for (;;) {
      const snapshot = await getDocs(
        query(
          collection(db, 'notifications'),
          where('userId', '==', userId),
          where('read', '==', false),
          limit(BATCH_SIZE)
        )
      );
      if (snapshot.empty) return;

      const batch = writeBatch(db);
      snapshot.docs.forEach(d => batch.update(d.ref, { read: true }));
      await batch.commit();

      if (snapshot.size < BATCH_SIZE) return;
    }
  }

  async deleteNotification(notificationId: string): Promise<void> {
    await deleteDoc(doc(db, 'notifications', notificationId));
  }

}

export const userService = new UserService();
